import React, { useEffect, useRef, useState } from 'react';
import { Send, X } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { ApiError } from '../../lib/api';
import { chatApi } from '../../lib/chat';
import { useSessionStream } from '../../hooks/useSessionStream';
import { toast } from 'sonner';
import { Button } from '../common/Button';
import type { ApiChatMessage } from '../../types/chat';

interface ConsultantChatProps {
  sessionId: string;
  userName: string;
  onClose: () => void;
}

export const ConsultantChat: React.FC<ConsultantChatProps> = ({ sessionId, userName, onClose }) => {
  const { user, setCredits } = useAuth();
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const { messages, connected, mergeMessages } = useSessionStream(sessionId);

  // Backfill histórico (GET) — o realtime veio do SSE
  useEffect(() => {
    let cancelled = false;

    const loadHistory = async () => {
      try {
        const { messages: history } = await chatApi.getMessages(sessionId);
        if (!cancelled) mergeMessages(history);
      } catch (err) {
        if (!cancelled) toast.error('Erro ao carregar mensagens');
        console.error('ConsultantChat: erro no backfill', err);
      }
    };

    loadHistory();
    return () => { cancelled = true; };
  }, [sessionId, mergeMessages]);

  // Autoscroll
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const handleSend = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newMessage.trim() || loading || !user) return;

    setLoading(true);
    try {
      // Server resolve o papel do remetente pelo cookie (consultant respondendo)
      const response = await chatApi.sendMessage(sessionId, newMessage);
      if (typeof response.credits === 'number') setCredits(response.credits);
      mergeMessages([response.userMsg]);
      setNewMessage('');
    } catch (err) {
      if (err instanceof ApiError && err.status === 402) {
        toast.error('Créditos insuficientes para continuar o chat');
      } else {
        toast.error(err instanceof Error ? err.message : 'Erro ao enviar mensagem');
      }
    } finally {
      setLoading(false);
    }
  };

  const isMine = (m: ApiChatMessage) => m.senderId === user?.id || m.senderRole === 'ai';

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50">
      <div className="bg-indigo-950 rounded-lg w-full max-w-2xl h-[80vh] flex flex-col">
        <div className="p-4 border-b border-purple-500/20">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-medium text-purple-200">
              Chat com {userName}
            </h3>
            <Button
              variant="secondary"
              size="sm"
              onClick={onClose}
              icon={<X className="w-4 h-4" />}
            >
              Fechar
            </Button>
          </div>
        </div>

        <div className="px-4 pb-2 text-xs text-purple-200/60">
          {connected ? 'conectado' : 'reconectando…'}
        </div>

        <div className="flex-1 overflow-y-auto p-4 space-y-4">
          {messages.map((message) => (
            <div
              key={message.id}
              className={`flex ${isMine(message) ? 'justify-end' : 'justify-start'}`}
            >
              <div
                className={`max-w-[80%] p-3 rounded-lg ${
                  isMine(message)
                    ? 'bg-purple-500 text-white'
                    : 'bg-white/10 text-purple-200'
                }`}
              >
                <p>{message.content}</p>
                <p className="text-xs opacity-70 mt-1">
                  {new Date(message.createdAt).toLocaleTimeString()}
                </p>
              </div>
            </div>
          ))}
          <div ref={messagesEndRef} />
        </div>

        <form onSubmit={handleSend} className="p-4 border-t border-purple-500/20">
          <div className="flex space-x-2">
            <input
              type="text"
              value={newMessage}
              onChange={(e) => setNewMessage(e.target.value)}
              placeholder="Digite sua mensagem..."
              className="flex-1 input-primary"
              disabled={loading}
            />
            <Button
              type="submit"
              disabled={loading || !newMessage.trim()}
              loading={loading}
              icon={<Send className="w-4 h-4" />}
            >
              Enviar
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};